import { useState } from 'react'
import { Icon } from '@iconify/react'
import { CpfCnpjInput } from '@/components/CpfCnpjInput'
import {
  addParticipatingCompany,
  ensureParticipatingCompanies,
  removeParticipatingCompany,
} from '@/lib/raspadinha-campaigns'
import type { RaspadinhaCampaign } from '@/types/raspadinha-campaign'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import tableStyles from './RaspadinhaGanhadoresSection.module.css'
import styles from './RaspadinhaEmpresasParticipantesSection.module.css'

type RaspadinhaEmpresasParticipantesSectionProps = {
  form: RaspadinhaCampaign
  onFormChange: (next: RaspadinhaCampaign) => void
}

export function RaspadinhaEmpresasParticipantesSection({
  form,
  onFormChange,
}: RaspadinhaEmpresasParticipantesSectionProps) {
  const companies = ensureParticipatingCompanies(form.participating_companies)
  const [cnpjInput, setCnpjInput] = useState('')
  const [isCnpjValid, setIsCnpjValid] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  const handleAddCompany = () => {
    const result = addParticipatingCompany(companies, cnpjInput)
    if (result.error) {
      setAddError(result.error)
      return
    }

    setAddError(null)
    setCnpjInput('')
    setIsCnpjValid(false)
    onFormChange({ ...form, participating_companies: result.companies })
  }

  const handleRemoveCompany = (companyId: string) => {
    onFormChange({
      ...form,
      participating_companies: removeParticipatingCompany(companies, companyId),
    })
  }

  return (
    <section className={editStyles.editSection} aria-labelledby="raspadinha-empresas">
      <h2 id="raspadinha-empresas" className={editStyles.editSectionTitle}>
        <Icon className={editStyles.editSectionTitleIcon} icon="mdi:domain" />
        Empresas Participantes
      </h2>

      <div className={editStyles.editSectionBody}>
        <p className={`${editStyles.editLabelHint} ${tableStyles.summaryHint}`}>
          Cadastre os CNPJs das empresas que participam desta campanha raspadinha.
        </p>

        <div className={styles.addCompanyRow}>
          <div className={styles.addCompanyField}>
            <label className={editStyles.editLabel} htmlFor="raspadinha-company-cnpj">
              CNPJ da empresa
            </label>
            <CpfCnpjInput
              id="raspadinha-company-cnpj"
              mode="cnpj"
              value={cnpjInput}
              onChange={(masked) => {
                setCnpjInput(masked)
                setAddError(null)
              }}
              onValidationChange={setIsCnpjValid}
              className={editStyles.editInput}
              placeholder="00.000.000/0000-00"
            />
          </div>
          <button
            type="button"
            className={styles.addCompanyButton}
            onClick={handleAddCompany}
            disabled={!isCnpjValid}
          >
            <Icon icon="mdi:plus" aria-hidden />
            Adicionar
          </button>
        </div>

        {addError ? (
          <p className={styles.addCompanyError} role="alert">
            {addError}
          </p>
        ) : null}

        <p className={`${editStyles.editLabelHint} ${tableStyles.summaryHint}`}>
          {companies.length} empresa(s) participante(s).
        </p>

        {companies.length === 0 ? (
          <p className={tableStyles.emptyState}>Nenhum CNPJ cadastrado nesta campanha.</p>
        ) : (
          <div className={tableStyles.tableWrap}>
            <table className={tableStyles.dataTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>CNPJ</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {companies.map((company, index) => (
                  <tr key={company.id}>
                    <td>
                      <span className={tableStyles.cellText}>{index + 1}</span>
                    </td>
                    <td>
                      <span className={tableStyles.cellText}>{company.cnpj}</span>
                    </td>
                    <td className={styles.actionsCell}>
                      <button
                        type="button"
                        className={styles.removeCompanyButton}
                        onClick={() => handleRemoveCompany(company.id)}
                        aria-label={`Remover CNPJ ${company.cnpj}`}
                      >
                        <Icon icon="mdi:trash-can-outline" aria-hidden />
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}
